import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import FullCalendar from "@fullcalendar/react"
import { DateTime } from "luxon"
import { ConfigQuery } from "../../../createOrderApi"

const REFRESH_THROTTLE_MS = 10 * 1000

const RefreshCalendarContext = createContext<{
    setCalendarRef: (ref: FullCalendar | null) => void
    triggerRefetch: () => void
    triggerThrottledRefetch: () => void
    goToMeeting: (start: DateTime) => void
} | null>(null)

type Props = Readonly<PropsWithChildren<{}>>

export function RefreshCalendarProvider({ children }: Props) {
    const calendarRef = useRef<FullCalendar | null>(null)
    const lastRefreshAtRef = useRef<number>(0)
    const queryClient = useQueryClient()

    const setCalendarRef = useCallback((ref: FullCalendar | null) => {
        calendarRef.current = ref
    }, [])

    const triggerRefetch = useCallback(() => {
        lastRefreshAtRef.current = DateTime.utc().toMillis()
        // day queries come from fetchQuery and have no observers, so invalidating alone never refetches
        queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
        queryClient.invalidateQueries({ queryKey: [ConfigQuery.RESOURCES_FREE_BUSY] })
        calendarRef.current?.getApi().refetchEvents()
    }, [queryClient])

    const triggerThrottledRefetch = useCallback(() => {
        if (DateTime.utc().toMillis() - lastRefreshAtRef.current < REFRESH_THROTTLE_MS) return
        triggerRefetch()
    }, [triggerRefetch])

    const goToMeeting = useCallback((start: DateTime) => {
        const api = calendarRef.current?.getApi()
        if (!api) return

        const { activeStart, activeEnd } = api.view
        const viewDays = Math.round(DateTime.fromJSDate(activeEnd).diff(DateTime.fromJSDate(activeStart), "days").days)
        // the meeting day sits in the middle of the window, as the day navigation keeps it
        const dayOffset = Math.max(0, Math.floor((viewDays - 1) / 2))

        api.gotoDate(start.startOf("day").minus({ days: dayOffset }).toJSDate())
        api.scrollToTime({ days: dayOffset, hours: start.hour, minutes: start.minute })
    }, [])

    const value = useMemo(
        () => ({
            setCalendarRef,
            triggerRefetch,
            triggerThrottledRefetch,
            goToMeeting,
        }),
        [setCalendarRef, triggerRefetch, triggerThrottledRefetch, goToMeeting],
    )

    return <RefreshCalendarContext.Provider value={value}>{children}</RefreshCalendarContext.Provider>
}

export function useRefreshCalendar() {
    const context = useContext(RefreshCalendarContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useRefreshCalendar must be used within a RefreshCalendarProvider. " +
                "Check that you have wrapped your component " +
                "in <RefreshCalendarProvider ...>.",
        )
    }

    return context
}
